import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Modal, Button, Image, InputGroup } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { userService } from '../services/userService';
import { accountService } from '../services/accountService';
import accountIcon from '../assets/images/icons/account.png'

const Profile = () => {
  const [userData, setUserData] = useState({
    username: "",
    email: "",
    contactNo: "",
    accountId: "",
    roles: []
  });
  const [companyName, setCompanyName] = useState("");
  const [error, setError] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [updateValidationErrors, setUpdateValidationErrors] = useState({
    email: '',
    contactNo: ''
  });
  const [updateData, setUpdateData] = useState({
    email: "",
    contactNo: "",
    roles: [],
    enabled: true,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: ""
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordValidationErrors, setPasswordValidationErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [passwordTouched, setPasswordTouched] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false
  });
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false
  });
  const availableRoles = ["USER", "ADMIN"];
  const [isEnabled, setIsEnabled] = useState(true);

  // Small inline eye icon component (uses Bootstrap-like SVG paths)
  const EyeIcon = ({ open = false }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 16 16"
      fill="currentColor"
    >
      {open ? (
        // eye-slash
        <path d="M13.359 11.238l1.43 1.43a.75.75 0 0 1-1.06 1.06l-1.638-1.638C10.86 12.79 9.5 13.5 8 13.5 3.5 13.5 1 8 1 8s.664-1.327 1.928-2.674L1.21 3.607a.75.75 0 1 1 1.06-1.06l12.52 12.52a.75.75 0 1 1-1.06 1.06l-1.37-1.37zM5.723 6.602l-.987-.987A4.5 4.5 0 0 0 3.06 8c0 .002 2 4.5 4.94 4.5.93 0 1.802-.31 2.563-.842l-1.08-1.08A3 3 0 0 1 5.723 6.602zM8 2.5c1.5 0 2.86.71 3.931 1.618l-1.1 1.1A4.488 4.488 0 0 0 8 4.5C5.06 4.5 3.06 8 3.06 8c0 .002.213.49.62 1.13l-1.1 1.1C1.664 9.327 1 8 1 8s2.5-5.5 7-5.5z" />
      ) : (
        // eye
        <path d="M8 2.5C3.5 2.5 1 8 1 8s2.5 5.5 7 5.5S15 8 15 8s-2.5-5.5-7-5.5zm0 9A3.5 3.5 0 1 1 8 5a3.5 3.5 0 0 1 0 7z" />
      )}
    </svg>
  );

  // Validation helpers
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateContactNo = (contactNo) => {
    // 10-digit numeric phone number
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(contactNo);
  };

  const validatePassword = (password) => {
    // Min 8 chars, at least one lowercase, uppercase, digit, special char
    if (!password || password.length < 8) return 'Password must be at least 8 characters';
    if (!/(?=.*[a-z])/.test(password)) return 'Must contain at least one lowercase letter';
    if (!/(?=.*[A-Z])/.test(password)) return 'Must contain at least one uppercase letter';
    if (!/(?=.*\d)/.test(password)) return 'Must contain at least one number';
    if (!/(?=.*[@$!%*?&])/.test(password)) return 'Must contain at least one special character';
    return '';
  };

  const validateUpdateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value) return 'Email is required';
        if (!validateEmail(value)) return 'Please enter a valid email address';
        return '';
      case 'contactNo':
        if (!value) return 'Contact number is required';
        if (!validateContactNo(value)) return 'Contact number must be 10 digits';
        return '';
      default:
        return '';
    }
  };

  const recomputePasswordErrors = (nextData) => {
    const errs = { currentPassword: '', newPassword: '', confirmNewPassword: '' };
    // Current password: required and min length (aligning to 8 for consistency)
    if (!nextData.currentPassword) {
      errs.currentPassword = 'Current password is required';
    } else if (nextData.currentPassword.length < 8) {
      errs.currentPassword = 'Current password must be at least 8 characters';
    }
    // New password complexity
    const pwdErr = validatePassword(nextData.newPassword);
    if (!nextData.newPassword) {
      errs.newPassword = 'New password is required';
    } else if (pwdErr) {
      errs.newPassword = pwdErr;
    }
    // Confirm match
    if (!nextData.confirmNewPassword) {
      errs.confirmNewPassword = 'Confirm new password is required';
    } else if (nextData.newPassword !== nextData.confirmNewPassword) {
      errs.confirmNewPassword = 'New passwords do not match';
    }
    // Prevent reuse of current password
    if (!errs.newPassword && nextData.currentPassword && nextData.newPassword === nextData.currentPassword) {
      errs.newPassword = 'New password must be different from current password';
    }
    return errs;
  };

  const isUpdateFormValid = () => {
    return (
      !updateValidationErrors.email &&
      !updateValidationErrors.contactNo &&
      updateData.email && updateData.contactNo
    );
  };

  const isPasswordFormValid = () => {
    return (
      !passwordValidationErrors.currentPassword &&
      !passwordValidationErrors.newPassword &&
      !passwordValidationErrors.confirmNewPassword &&
      passwordData.currentPassword && passwordData.newPassword && passwordData.confirmNewPassword
    );
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    // Validate fields
    const errs = recomputePasswordErrors(passwordData);
    setPasswordValidationErrors(errs);
    // When attempting submit, mark all as touched so the user sees relevant errors
    setPasswordTouched({ currentPassword: true, newPassword: true, confirmNewPassword: true });
    const hasErrors = Object.values(errs).some((msg) => msg);
    if (hasErrors) {
      setPasswordError('Please fix validation errors before submitting');
      return;
    }

    try {
      await userService.changePassword({
        username: userData.username,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmNewPassword: passwordData.confirmNewPassword
      });

      setPasswordSuccess("Password changed successfully");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: ""
      });
      setTimeout(() => {
        setShowPasswordModal(false);
      }, 2000);
    } catch (err) {
      setPasswordError(err.response?.data?.message || "Failed to change password");
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdateError("");
    setUpdateSuccess("");
    // Validate fields
    const emailErr = validateUpdateField('email', updateData.email);
    const contactErr = validateUpdateField('contactNo', updateData.contactNo);
    setUpdateValidationErrors({ email: emailErr, contactNo: contactErr });
    if (emailErr || contactErr) {
      setUpdateError('Please fix validation errors before submitting');
      setUpdateTouched({ email: true, contactNo: true });
      return;
    }

    try {
      await userService.updateRegisteredUser(userData.id, {
        email: updateData.email,
        contactNo: updateData.contactNo,
        roles: updateData.roles,
        enabled: true
      });

      setUpdateSuccess("Profile updated successfully");
      setUserData(prev => ({
        ...prev,
        email: updateData.email,
        contactNo: updateData.contactNo,
        roles: updateData.roles
      }));
      
      setTimeout(() => {
        setShowUpdateModal(false);
      }, 2000);
    } catch (err) {
      setUpdateError(err.response?.data?.message || "Failed to update profile");
    }
  };

  const handleUpdateModalShow = () => {
    setUpdateData({
      email: userData.email,
      contactNo: userData.contactNo,
      roles: userData.roles
    });
    setUpdateValidationErrors({
      email: '',
      contactNo: ''
    });
    setShowUpdateModal(true);
  };

  const handleUpdateFieldChange = (e) => {
    const { name, value } = e.target;
    const next = { ...updateData, [name]: value };
    setUpdateData(next);
    const err = validateUpdateField(name, value);
    setUpdateValidationErrors((prev) => ({ ...prev, [name]: err }));
  };

  const [updateTouched, setUpdateTouched] = useState({ email: false, contactNo: false });

  const handleUpdateFieldBlur = (e) => {
    const { name } = e.target;
    setUpdateTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handlePasswordFieldChange = (name, value) => {
    const next = { ...passwordData, [name]: value };
    setPasswordData(next);
    const errs = recomputePasswordErrors(next);
    setPasswordValidationErrors(errs);
  };

  const markPasswordTouched = (name) => {
    setPasswordTouched((prev) => ({ ...prev, [name]: true }));
  };

  const toggleShowPassword = (name) => {
    setShowPassword((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No token found');
        }

        const decoded = jwtDecode(token);
        const username = decoded.sub;
        
        const userResponse = await userService.getUserByUsername(username);

        if (userResponse) {
          setUserData(userResponse);
          
          // Only fetch company details if user has an accountId
          if (userResponse.accountId) {
            try {
              const companyResponse = await accountService.getAccountConfigById(userResponse.accountId);
              setCompanyName(companyResponse.accountName);
            } catch (companyError) {
              console.error('Error fetching company details:', companyError);
            }
          } else {
          }
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch user data");
      }
    };

    fetchUserData();
  }, []);

  return (
<Container
      fluid
      className="ps-0 pb-4 pe-0 overflow-hidden user-management-container bg-white"
    >
            <Card className="card-nostyle">
        <Card.Body className="ps-0 pe-0">
          <div className="custom-domain-containers">
            <div
              className="d-flex justify-content-between ps-4 pe-4 align-items-center"
              style={{ borderBottom: "1px solid #EAECF0" }}
            >
              <h4 className="mb-0 bg-white br-radius-12 rounded-bottom-0 pb-3 fs-20 poppins fw-600">
                <Image src={accountIcon} className="me-1" width="20" />{" "}
                Account
              </h4>
            </div>
          </div>
        </Card.Body>
      </Card>

<div className="ps-4 pe-4">
      <Row className="mt-2">
        <Col md={10}>
          <Card className="br-radius-12" style={{ boxShadow: '0px 4px 24px 0px rgba(18, 18, 18, 0.04)' }}>
            <Card.Header className="bg-transparent pt-3 pb-3" style={{ borderBottom: '1px solid #e5e5e5' }}>
              <h4 className="mb-0 GeneralSansSemibold fs-16">Profile Settings</h4>
            </Card.Header>
            <Card.Body className="p-4">
              {error && <div className="alert alert-danger mb-3">{error}</div>}
              <Form>
              <Row>
               <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">Username</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={userData.username}
                    disabled
                    className="br-color-EAECEE"
                    style={{ padding: '10px 16px', backgroundColor: '#f8f9fa' }}
                  />
                </Form.Group>
                </Col>

                <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">Email</Form.Label>
                  <Form.Control 
                    type="email" 
                    value={userData.email}
                    disabled
                    className="br-color-EAECEE"
                    style={{ padding: '10px 16px', backgroundColor: '#f8f9fa' }}
                  />
                </Form.Group>
</Col>
    <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">Contact Number</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={userData.contactNo}
                    disabled
                    className="br-color-EAECEE"
                    style={{ padding: '10px 16px', backgroundColor: '#f8f9fa' }}
                  />
                </Form.Group>
</Col>
      

<Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">Company Name</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={companyName}
                    disabled
                    className="br-color-EAECEE"
                    style={{ padding: '10px 16px', backgroundColor: '#f8f9fa' }}
                  />
                </Form.Group>
</Col>

    <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161 form-label">Role</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={userData.roles.join(", ")}
                    disabled
                    className="br-color-EAECEE"
                    style={{ padding: '10px 16px', backgroundColor: '#f8f9fa' }}
                  />
                </Form.Group>
</Col>
<Col md={12}>
                <div className="mt-4 d-flex gap-2 justify-content-end">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowPasswordModal(true)}
                    style={{ backgroundColor: '#00C76F', color: 'white' }}
                  >
                    Change Password
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={handleUpdateModalShow}
                    style={{ backgroundColor: '#00C76F', color: 'white' }}
                  >
                    Update Profile
                  </button>
                </div>
      </Col>
      </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} aria-labelledby="contained-modal-title-vcenter"
      centered>
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
          Change Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          {passwordError && <div className="alert alert-danger">{passwordError}</div>}
          {passwordSuccess && <div className="alert alert-success">{passwordSuccess}</div>}
          <Form onSubmit={handlePasswordChange}>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">Current Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword.currentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordFieldChange('currentPassword', e.target.value)}
                  onBlur={() => markPasswordTouched('currentPassword')}
                  className={(passwordValidationErrors.currentPassword && passwordTouched.currentPassword) ? 'is-invalid' : ''}
                  required
                />
                <Button
                  variant="light"
                  className="border"
                  onClick={() => toggleShowPassword('currentPassword')}
                  aria-label={showPassword.currentPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon open={showPassword.currentPassword} />
                </Button>
              </InputGroup>
              {(passwordValidationErrors.currentPassword && passwordTouched.currentPassword) && (
                <div className="invalid-feedback d-block">{passwordValidationErrors.currentPassword}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword.newPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordFieldChange('newPassword', e.target.value)}
                  onBlur={() => markPasswordTouched('newPassword')}
                  className={(passwordValidationErrors.newPassword && passwordTouched.newPassword) ? 'is-invalid' : ''}
                  required
                />
                <Button
                  variant="light"
                  className="border"
                  onClick={() => toggleShowPassword('newPassword')}
                  aria-label={showPassword.newPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon open={showPassword.newPassword} />
                </Button>
              </InputGroup>
              {(passwordValidationErrors.newPassword && passwordTouched.newPassword) && (
                <div className="invalid-feedback d-block">{passwordValidationErrors.newPassword}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">Confirm New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword.confirmNewPassword ? 'text' : 'password'}
                  value={passwordData.confirmNewPassword}
                  onChange={(e) => handlePasswordFieldChange('confirmNewPassword', e.target.value)}
                  onBlur={() => markPasswordTouched('confirmNewPassword')}
                  className={(passwordValidationErrors.confirmNewPassword && passwordTouched.confirmNewPassword) ? 'is-invalid' : ''}
                  required
                />
                <Button
                  variant="light"
                  className="border"
                  onClick={() => toggleShowPassword('confirmNewPassword')}
                  aria-label={showPassword.confirmNewPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon open={showPassword.confirmNewPassword} />
                </Button>
              </InputGroup>
              {(passwordValidationErrors.confirmNewPassword && passwordTouched.confirmNewPassword) && (
                <div className="invalid-feedback d-block">{passwordValidationErrors.confirmNewPassword}</div>
              )}
            </Form.Group>
            <Button 
              type="submit"
              className="float-end" 
              style={{ backgroundColor: '#00C76F', border: 'none' }}
              disabled={!isPasswordFormValid()}
            >
              Change Password
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} aria-labelledby="contained-modal-title-vcenter"
      centered>
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
          Update Profile
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4 pt-0">
          {updateError && <div className="alert alert-danger">{updateError}</div>}
          {updateSuccess && <div className="alert alert-success">{updateSuccess}</div>}
          <Form onSubmit={handleUpdateProfile}>
            <Form.Group className="mb-2">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">Email</Form.Label>
              <Form.Control
                type="email"
                value={updateData.email}
                onChange={handleUpdateFieldChange}
                onBlur={handleUpdateFieldBlur}
                name="email"
                className={`br-color-EAECEE ${(updateValidationErrors.email && updateTouched.email) ? 'is-invalid' : ''}`}
                required
              />
              {(updateValidationErrors.email && updateTouched.email) && (
                <div className="invalid-feedback d-block">{updateValidationErrors.email}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">Contact Number</Form.Label>
              <Form.Control
                type="text"
                value={updateData.contactNo}
                onChange={handleUpdateFieldChange}
                onBlur={handleUpdateFieldBlur}
                name="contactNo"
                className={`br-color-EAECEE ${(updateValidationErrors.contactNo && updateTouched.contactNo) ? 'is-invalid' : ''}`}
                required
              />
              {(updateValidationErrors.contactNo && updateTouched.contactNo) && (
                <div className="invalid-feedback d-block">{updateValidationErrors.contactNo}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">Role</Form.Label>
              <Form.Select
                value={updateData.roles[0]}
                onChange={(e) => setUpdateData({
                  ...updateData,
                  roles: [e.target.value]
                })}
                className="br-color-EAECEE"
                required
              >
                {availableRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <div className="d-flex gap-2 w-75 m-auto mt-4">
              <Button
                variant="light"
                onClick={() => setShowUpdateModal(false)}
                className="flex-grow-1 br-radius-8"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                style={{ backgroundColor: '#00C76F', border: 'none' }}
                className="flex-grow-1 br-radius-8"
                disabled={!isUpdateFormValid()}
              >
                Update Profile
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      </div>
    </Container>
  );
};

export default Profile;